import * as React from 'react'
import { Box, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, useTheme } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import HeaderTableCell from '../../components/common/StyledTableCell/StyledTableCell';
import UnstyledLink from '../../components/common/UnstyledLink/UnstyledLink';
import { PieChart } from '@mui/x-charts';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Link, useLoaderData, useNavigate } from 'react-router-dom';

import AssetInfoChip from '../Assets/AssetInfoChip';
import { assetsApi, inspectionsApi, statsApi } from '../../api-interface';
import { AnalysisResponse, AssetComplianceSnapshot, AssetReminderReturnType, AssetResponse, ReminderDueSoonResponseType } from '../../api';
import { TenantContext } from '../..';
import { reminderTypeLabels } from '../../components/common/reminderTypeLabels';


export async function loader(tenant_id) {
    const assets = await assetsApi(tenant_id).getAssetsAssetsGet();
    const inspections = await inspectionsApi(tenant_id).getAnalysesInspectionsGet();
    const reminders = await assetsApi(tenant_id).getDueSoonRemindersAssetsRemindersDueSoonGet();
    var complianceOverTime = await statsApi(tenant_id).getComplianceOverTimeStatsComplianceOverTimeGet();
    complianceOverTime.data.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const ret = {
        assets: assets.data,
        inspections: inspections.data,
        complianceOverTime: complianceOverTime.data,
        reminders: reminders.data
    };
    return ret;
}

export const engineerData = [
    { label: 'Alice', value: 25 },
    { label: 'Bob', value: 1 },
    { label: 'Charlie', value: 10 },
];

export const valueFormatter = (item: { value: number }) => `${item.value}`;


const Dashboard = () => {
    const theme = useTheme();
    const { assets, inspections, complianceOverTime, reminders } = useLoaderData() as {
        assets: AssetResponse[];
        inspections: AnalysisResponse[];
        complianceOverTime: AssetComplianceSnapshot[];
        reminders: ReminderDueSoonResponseType[];
    };
    const tenant_id = React.useContext(TenantContext);
    const navigate = useNavigate();

    const compliantAssets = assets.filter(asset => asset.complaint === 'pass').length;
    const warningAssets = assets.filter(asset => asset.complaint === 'warning').length;
    const needsAttentionAssets = assets.filter(asset => asset.complaint === 'fail').length;
    const totalAssets = assets.length;
    const nonCompliantInspectionsByEngineer = inspections.reduce((acc, inspection) => {
        if (inspection.analysis.outcome !== 'pass') {
            acc[String(inspection.analysis.engineer_name)] = (acc[String(inspection.analysis.engineer_name)] || 0) + 1;
        }
        return acc;
    }, {} as Record<string, number>);
    const engineerData = Object.entries(nonCompliantInspectionsByEngineer).map(([engineer, count]) => ({
        label: engineer,
        value: count,
    })).sort((a, b) => b.value - a.value);

    const assetsNearlyDue = assets.filter(asset => asset.most_recent_validation?.next_inspection_due && new Date(String(asset.most_recent_validation.next_inspection_due)) < new Date(new Date().setMonth(new Date().getMonth() + 1)))

    return (
        <Box display="flex" flexDirection="column" gap={5}>
            <Typography variant="h4">Dashboard</Typography>
            <Grid container spacing={2}>
                <Grid container spacing={2} size={{ 'xs': 12, 'sm': 6, 'md': 6, 'lg': 3 }} sx={{ mb: 2, cursor: 'pointer' }} onClick={() => navigate(`/dashboard/${tenant_id}/assets?compliant=pass`)}>
                    <AssetInfoChip
                        left={<Typography fontWeight={500} fontSize={"1.25rem"}>Compliant Assets</Typography>}
                        right={<Typography fontSize={"1.25rem"} color={theme.palette.success.main} fontWeight={"bold"} whiteSpace={'nowrap'}>
                            <TaskAltIcon fontSize='inherit' /> {compliantAssets}
                        </Typography>}
                        bgcolor={theme.palette.successBackground.main}
                        borderColor={theme.palette.successOutline.main}
                        to={`/dashboard/${tenant_id}/assets?compliant=pass`}
                    />
                </Grid>
                <Grid container spacing={2} size={{ 'xs': 12, 'sm': 6, 'md': 6, 'lg': 3 }} sx={{ mb: 2, cursor: 'pointer' }} onClick={() => navigate(`/dashboard/${tenant_id}/assets?compliant=warning`)}>
                    <AssetInfoChip
                        left={<Typography fontWeight={500} fontSize={"1.25rem"}>Assets With Warning</Typography>}
                        right={<Typography fontSize={"1.25rem"} color={theme.palette.warning.main} fontWeight={"bold"} whiteSpace={'nowrap'}>
                            <ErrorOutlineIcon fontSize='inherit' /> {warningAssets}
                        </Typography>}
                        bgcolor={theme.palette.warningBackground.main}
                        borderColor={theme.palette.warningOutline.main}
                    />
                </Grid>
                <Grid container spacing={2} size={{ 'xs': 12, 'sm': 6, 'md': 6, 'lg': 3 }} sx={{ mb: 2, cursor: 'pointer' }} onClick={() => navigate(`/dashboard/${tenant_id}/assets?compliant=fail`)}>
                    <AssetInfoChip
                        left={<Typography fontWeight={500} fontSize={"1.25rem"}>Assets Requiring Attention</Typography>}
                        right={<Typography fontSize={"1.25rem"} color={theme.palette.error.main} fontWeight={"bold"} whiteSpace={'nowrap'}>
                            <ErrorOutlineIcon fontSize='inherit' /> {needsAttentionAssets}
                        </Typography>}
                        bgcolor={theme.palette.errorBackground.main}
                        borderColor={theme.palette.errorOutline.main}
                    />
                </Grid>
                <Grid container spacing={2} size={{ 'xs': 12, 'sm': 6, 'md': 6, 'lg': 3 }} sx={{ mb: 2, cursor: 'pointer' }} onClick={() => navigate(`/dashboard/${tenant_id}/assets`)}>

                    <AssetInfoChip
                        left={<Typography fontWeight={500} fontSize={"1.25rem"}>Total Assets</Typography>}
                        right={<Typography fontSize={"1.25rem"} color={theme.palette.primary.main} fontWeight={"bold"} whiteSpace={'nowrap'}>
                            <ErrorOutlineIcon fontSize='inherit' /> {totalAssets}
                        </Typography>}
                        bgcolor={theme.palette.primary.light}
                        borderColor={theme.palette.primary.main}
                    />
                </Grid>
                <Grid size={{ 'xs': 12, 'md': 6 }} sx={{ mt: 5 }}>
                    <Typography align="center" variant="h5">
                        Asset Compliance Over Time
                    </Typography>
                    <LineChart
                        sx={{
                            width: '100%',
                            '& .MuiAreaElement-series-compliant': {
                                fill: theme.palette.successBackground.main,
                                filter: 'none'
                            },
                            '& .MuiAreaElement-series-warning': {
                                fill: theme.palette.warningBackground.main,
                                filter: 'none'
                            },
                            '& .MuiAreaElement-series-needs_attention': {
                                fill: theme.palette.errorBackground.main,
                                filter: 'none'
                            }
                        }}
                        dataset={complianceOverTime.map((item) => ({
                            date: new Date(item.date),
                            compliant: item.compliant_assets,
                            warning: item.assets_with_warning,
                            needs_attention: item.assets_requiring_attention,
                        }))}
                        xAxis={[
                            {
                                id: 'Years',
                                dataKey: 'date',
                                scaleType: 'time',
                                // Minimum date in the dataset
                                max: new Date(complianceOverTime[complianceOverTime.length - 1]?.date || Date.now()),
                                min: new Date(complianceOverTime[0]?.date || Date.now()),
                                valueFormatter: (date) => date.toLocaleDateString('en-GB', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                }),
                            },
                        ]}
                        yAxis={[
                            {
                                width: 50,
                            },
                        ]}
                        series={[
                            {
                                id: 'compliant',
                                label: 'Compliant Assets',
                                dataKey: 'compliant',
                                stack: 'total',
                                area: true,
                                showMark: false,
                                color: theme.palette.successOutline.main,

                            },
                            {
                                id: 'warning',
                                label: 'Assets with Warning',
                                dataKey: 'warning',
                                stack: 'total',
                                area: true,
                                showMark: false,
                                color: theme.palette.warningOutline.main,
                            },
                            {
                                id: 'needs_attention',
                                label: 'Assets Requiring Attention',
                                dataKey: 'needs_attention',
                                stack: 'total',
                                area: true,
                                showMark: false,
                                color: theme.palette.errorOutline.main,
                            },
                        ]}
                        height={400}
                    />
                </Grid>
                <Grid size={{ 'xs': 12, 'md': 6 }} sx={{ mt: 5 }}>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Typography align="center" variant="h5">
                            Non Compliant LGSR Inspections by Engineer
                        </Typography>
                        <PieChart
                            height={400}
                            width={600}
                            colors={[
                                theme.palette.primary.light,
                                theme.palette.pastellePurple.main,
                                theme.palette.pastelleOrange.main
                            ]}
                            series={[
                                {
                                    data: engineerData,
                                    innerRadius: 100,
                                    arcLabelMinAngle: 20,
                                    valueFormatter,

                                },
                            ]}
                            onItemClick={(event, d) => {
                                const engineerName = engineerData[d.dataIndex].label;
                                navigate(`/dashboard/${tenant_id}/inspections?engineer=${encodeURIComponent(engineerName)}`);
                            }
                            }
                        />
                    </Box>
                </Grid>

                {/* Bottom row: Three equally sized components */}
                <Grid size={{ 'xs': 12, 'md': 6 }} sx={{ mt: 5 }} >
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Typography variant="h5" align="center">
                            Assets Requiring Attention
                        </Typography>
                        <TableContainer component={Paper}>
                            <Table aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <HeaderTableCell sx={{ backgroundColor: 'black', color: 'white' }}>
                                            Asset
                                        </HeaderTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {assets.filter(asset => asset.complaint === 'fail').map((asset) => (
                                        <TableRow key={asset.asset.id}>
                                            <TableCell>
                                                <UnstyledLink to={"/dashboard/" + tenant_id + "/assets/" + asset.asset.id}>
                                                    <Typography>{asset.asset.address_1}</Typography>
                                                </UnstyledLink>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Grid>

                <Grid size={{ 'xs': 12, 'md': 6 }} sx={{ mt: 5 }}>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Typography variant="h5" align="center">
                            Assets Soon Requiring LGSR Inspection
                        </Typography>
                        <TableContainer component={Paper}>
                            <Table aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <HeaderTableCell sx={{ backgroundColor: 'black', color: 'white' }}>
                                            Asset
                                        </HeaderTableCell>
                                        <HeaderTableCell sx={{ backgroundColor: 'black', color: 'white' }}>
                                            Next LGSR Inspection Due
                                        </HeaderTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {assetsNearlyDue.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={2} align="center">
                                                <Typography>No assets nearly due for inspection</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {assetsNearlyDue.map((asset) => (
                                        <TableRow key={asset.asset.id}>
                                            <TableCell>
                                                <UnstyledLink to={"/dashboard/" + tenant_id + "/assets/" + asset.asset.id}>
                                                    <Typography>{asset.asset.address_1}</Typography>
                                                </UnstyledLink>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>
                                                    {asset.most_recent_validation?.next_inspection_due ? new Date(String(asset.most_recent_validation.next_inspection_due)).toLocaleDateString('en-GB', {
                                                        day: 'numeric',
                                                        month: 'long',
                                                        year: 'numeric',
                                                    }) : 'N/A'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Grid>
                <Grid size={{ 'xs': 12, 'md': 6 }} sx={{ mt: 5 }}>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Typography variant="h5" align="center">
                            Upcoming Asset Reminders
                        </Typography>
                        <TableContainer component={Paper}>
                            <Table aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <HeaderTableCell sx={{ backgroundColor: 'black', color: 'white' }}>
                                            Asset
                                        </HeaderTableCell>
                                        <HeaderTableCell sx={{ backgroundColor: 'black', color: 'white' }}>
                                            Reminder Type
                                        </HeaderTableCell>
                                        <HeaderTableCell sx={{ backgroundColor: 'black', color: 'white' }}>
                                            Reminder Date
                                        </HeaderTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reminders.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={2} align="center">
                                                <Typography>No upcoming reminders</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {reminders.map((reminder) => (
                                        <TableRow key={reminder.id}>
                                            <TableCell>
                                                <UnstyledLink to={"/dashboard/" + tenant_id + "/assets/" + reminder.asset_id}>
                                                    <Typography>{reminder.asset_address}</Typography>
                                                </UnstyledLink>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>{reminderTypeLabels[reminder.reminder_type] || reminder.reminder_type}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>
                                                    {reminder.reminder_date ? new Date(String(reminder.reminder_date)).toLocaleDateString('en-GB', {
                                                        day: 'numeric',
                                                        month: 'long',
                                                        year: 'numeric',
                                                    }) : 'N/A'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Dashboard;